/**
 * Lorem Ipsum generator utility for placeholder content
 */

const LOREM_WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
  'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
  'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
  'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
  'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum'
];

export class LoremGenerator {
  private words: string[] = [...LOREM_WORDS];

  /**
   * Generate random Lorem Ipsum words
   */
  generateWords(count: number): string {
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * this.words.length);
      result.push(this.words[randomIndex]);
    }
    return result.join(' ');
  }

  /**
   * Generate Lorem Ipsum sentences
   */
  generateSentences(count: number, wordsPerSentence: number = 12): string {
    const sentences: string[] = [];
    for (let i = 0; i < count; i++) {
      const wordCount = wordsPerSentence + Math.floor(Math.random() * 8) - 4;
      let sentence = this.generateWords(wordCount);
      sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
      sentences.push(sentence);
    }
    return sentences.join(' ');
  }

  /**
   * Generate Lorem Ipsum paragraphs
   */
  generateParagraphs(count: number, sentencesPerParagraph: number = 5): string {
    const paragraphs: string[] = [];
    for (let i = 0; i < count; i++) {
      const sentenceCount = sentencesPerParagraph + Math.floor(Math.random() * 3) - 1;
      paragraphs.push(this.generateSentences(sentenceCount));
    }
    return paragraphs.join('\n\n');
  }

  /**
   * Generate a title
   */
  generateTitle(wordCount: number = 3): string {
    const title = this.generateWords(wordCount);
    return title
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Generate project data with Lorem Ipsum
   */
  generateProject(index: number) {
    return {
      title: `Lorem Project ${String.fromCharCode(65 + index)}`,
      subtitle: this.generateTitle(2),
      description: this.generateSentences(2),
      tags: this.generateWords(3).split(' '),
      media: `https://via.placeholder.com/600x400?text=Project+${index + 1}`
    };
  }

  /**
   * Generate multiple projects
   */
  generateProjects(count: number) {
    const projects = [];
    for (let i = 0; i < count; i++) {
      projects.push(this.generateProject(i));
    }
    return projects;
  }

  /**
   * Generate contact information
   */
  generateContactInfo() {
    return {
      address: '123 Lorem Street, Ipsum City, Dolor 12345',
      emails: [
        'lorem@brandnewbroadcast.com',
        'ipsum@brandnewbroadcast.com'
      ],
      phones: [
        '+1 234 567 8900',
        '+1 234 567 8901'
      ]
    };
  }

  /**
   * Generate complete content for all sections
   */
  generateSiteContent() {
    return {
      navigation: [
        { text: 'About', href: '#about' },
        { text: 'Previous Work', href: '#previous-work' },
        { text: 'Contact', href: '#contact' }
      ],
      about: {
        heading: 'Lorem Ipsum Dolor',
        subheading: 'Consectetur adipiscing elit',
        body: this.generateParagraphs(2),
        ctas: [
          { text: 'Get Started', href: '#contact', type: 'primary' as const },
          { text: 'Learn More', href: '#previous-work', type: 'secondary' as const }
        ]
      },
      previousWork: {
        heading: 'Previous Work',
        items: this.generateProjects(8)
      },
      contact: {
        heading: 'Contact',
        ...this.generateContactInfo()
      }
    };
  }
}

// Export singleton instance
export const loremGenerator = new LoremGenerator();

// Export convenience functions
export const generateWords = (count: number) => loremGenerator.generateWords(count);
export const generateSentences = (count: number, wordsPerSentence?: number) =>
  loremGenerator.generateSentences(count, wordsPerSentence);
export const generateParagraphs = (count: number, sentencesPerParagraph?: number) =>
  loremGenerator.generateParagraphs(count, sentencesPerParagraph);
export const generateTitle = (wordCount?: number) => loremGenerator.generateTitle(wordCount);
export const generateSiteContent = () => loremGenerator.generateSiteContent();